import React from "react";
import { Button } from "@/components/ui/button";
import { prefersReducedMotion } from "@/main/util/motionPreference";
import type { Localizer } from "@/main/react/Types";
import type { OpeningThoughtWithGroupMatchesDTO } from "@/main/react/discussion/OpeningThoughtGroupTypes";

/**
 * Round 37 `2a` (FI-583, D1/D9): "New since your last visit" on a thread the member has read before.
 *
 * `viewerLastVisitedAt` is the viewer's PREVIOUS visit, carried on both delivery paths (the
 * `data-viewer-last-visited-at` attribute on a direct load, `viewerLastVisitedAt` on /page-data) and null for an
 * anonymous viewer or a first visit. Mechanism: `designs/01-now/2026-09-01-engagement-tier2-continue-reading-cue-design.md`,
 * round 37 section.
 */
export const NEW_SINCE_LAST_VISIT_DIVIDER_ID = "discussion-new-since-last-visit";

function toEpochMs(value: unknown): number {
    if (typeof value === "number") {
        // An Instant can arrive as epoch seconds (with a fraction) from a serializer configured that way.
        return value > 1e12 ? value : value * 1000;
    }
    return typeof value === "string" && value.trim() ? Date.parse(value) : Number.NaN;
}

/**
 * The first top-level thought, in DISPLAY order, written after the previous visit by someone other than the viewer.
 * Undefined when there was no previous visit or no loaded thought qualifies, and then nothing renders.
 */
export function findFirstNewThoughtPublicId(
    items: readonly OpeningThoughtWithGroupMatchesDTO[],
    viewerLastVisitedAt: string | null | undefined,
    viewerUsername: string | undefined,
    excludedPublicIds: ReadonlySet<string>,
): string | undefined {
    const since = toEpochMs(viewerLastVisitedAt);
    if (!Number.isFinite(since)) {
        return undefined;
    }
    for (const item of items) {
        const thought = item?.openingArgument;
        const publicId = thought?.publicId;
        if (!thought || !publicId || excludedPublicIds.has(publicId) || thought.isDeleted) {
            continue;
        }
        if (viewerUsername && thought.username === viewerUsername) {
            continue;
        }
        const createdAt = toEpochMs(thought.createdAt);
        if (Number.isFinite(createdAt) && createdAt > since) {
            return publicId;
        }
    }
    return undefined;
}

/** A button, never a `#hash` link: jumping must not write a history entry (rule 33). */
export function JumpToNewRepliesButton({ localizer }: { localizer: Localizer }) {
    const onJump = React.useCallback(() => {
        const divider = document.getElementById(NEW_SINCE_LAST_VISIT_DIVIDER_ID);
        if (!divider) {
            return;
        }
        divider.scrollIntoView({ block: "start", behavior: prefersReducedMotion() ? "auto" : "smooth" });
        divider.focus({ preventScroll: true });
    }, []);

    return (
        <div className="flex">
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onJump}
                data-playwright-id="discussion-jump-to-new-replies"
            >
                {localizer("discussion.last_visit.jump")}
            </Button>
        </div>
    );
}

export function NewSinceLastVisitDivider({ localizer }: { localizer: Localizer }) {
    const label = localizer("discussion.last_visit.divider");
    return (
        <div
            id={NEW_SINCE_LAST_VISIT_DIVIDER_ID}
            role="separator"
            aria-label={label}
            // Focusable by script only, so the jump button can move a keyboard or screen-reader user here.
            tabIndex={-1}
            data-playwright-id="discussion-new-since-last-visit"
            className="my-3 flex scroll-mt-24 items-center gap-3 text-xs font-medium text-muted-foreground outline-none"
        >
            <span aria-hidden={true} className="h-px flex-1 bg-border" />
            <span aria-hidden={true}>{label}</span>
            <span aria-hidden={true} className="h-px flex-1 bg-border" />
        </div>
    );
}
